import { api } from './client'
import type { BulkSendResult, SendMessageInput } from './messages'
import type { Contact } from './types'

export type ListType = 'manual' | 'dynamic'

/** One AND-ed condition of a dynamic list (workflow-conditions format: {field, op, value}). */
export interface ListCriterion {
  /** owner_user_id | lead_source | do_not_email | do_not_sms | custom.<key> */
  field: string
  /** eq | ne | contains | is_true | is_false | is_set (allowed set depends on field) */
  op: string
  /** Required for eq | ne | contains. */
  value?: unknown
}

export interface ContactList {
  id: string
  name: string
  description: string | null
  listType: ListType
  /** Dynamic lists only; empty for manual. */
  criteria: ListCriterion[] | null
  /** Members the caller can see (dynamic lists compute them on the fly). */
  memberCount: number
  folderId: string | null
  folderName: string | null
  folderColor: string | null
  /** Who looks after the list — its creator until reassigned. */
  ownerUserId: string | null
  /** User who created the list; null for lists made before this was tracked. */
  creatorUserId: string | null
  /** Sequences the list was enrolled into ("Used in"). */
  sequenceCount: number
  /** When a campaign last went to the list; null = never. */
  lastSentAt: string | null
  createdAt: string
  updatedAt: string
}

export interface ListFolder {
  id: string
  name: string
  color: string | null
  listCount: number
}

export interface ListStats {
  total: number
  dynamic: number
  manual: number
  folders: number
}

/** A sequence the list was enrolled into. */
export interface ListUsage {
  sequenceId: string
  sequenceName: string
  lastEnrolledAt: string
}

/** One page of lists for the table view (offset pagination + total). */
export interface ListPage {
  items: ContactList[]
  total: number
  page: number
  size: number
}

export interface ListPageParams {
  page: number
  size: number
  sort: string // name | updatedAt | createdAt | listType
  dir: 'asc' | 'desc'
  q?: string
  folderId?: string
  type?: ListType
  ownerId?: string
}

/** PUT is a full replace; switching a list to dynamic deletes its manual members. */
export interface ListInput {
  name: string
  description?: string | null
  listType?: ListType
  criteria?: ListCriterion[]
  folderId?: string | null
  /** Omit to keep (update) or default to you (create). */
  ownerUserId?: string | null
}

export async function listLists(): Promise<ContactList[]> {
  return (await api.get<ContactList[]>('/api/lists')).data
}

export async function listListsPage(p: ListPageParams): Promise<ListPage> {
  const params: Record<string, string | number> = { page: p.page, size: p.size, sort: p.sort, dir: p.dir }
  if (p.q?.trim()) params.q = p.q.trim()
  if (p.folderId) params.folderId = p.folderId
  if (p.type) params.type = p.type
  if (p.ownerId) params.ownerId = p.ownerId
  return (await api.get<ListPage>('/api/lists/page', { params })).data
}

export async function getList(id: string): Promise<ContactList> {
  return (await api.get<ContactList>(`/api/lists/${id}`)).data
}

export async function getListStats(): Promise<ListStats> {
  return (await api.get<ListStats>('/api/lists/stats')).data
}

/** A send to the list and how it did (sent excludes skipped contacts; opened/clicked count emails, not hits). */
export interface ListCampaign {
  id: string
  channel: 'email' | 'sms'
  subject: string | null
  body: string
  sentByName: string | null
  recipients: number
  sent: number
  bounced: number
  opened: number
  clicked: number
  createdAt: string
}

export async function getListCampaigns(id: string): Promise<ListCampaign[]> {
  return (await api.get<ListCampaign[]>(`/api/lists/${id}/campaigns`)).data
}

export async function getListUsage(id: string): Promise<ListUsage[]> {
  return (await api.get<ListUsage[]>(`/api/lists/${id}/usage`)).data
}

/** How many contacts you can see match these criteria (live count while editing a dynamic list). */
export async function countMatching(criteria: ListCriterion[]): Promise<number> {
  return (await api.post<{ count: number }>('/api/lists/count', { criteria })).data.count
}

/** keepMembers: a dynamic copy keeps the criteria, a manual copy the members. */
export async function duplicateList(
  id: string,
  input: { name: string; folderId: string | null; keepMembers: boolean },
): Promise<ContactList> {
  return (await api.post<ContactList>(`/api/lists/${id}/duplicate`, input)).data
}

export async function moveList(id: string, folderId: string | null): Promise<ContactList> {
  return (await api.put<ContactList>(`/api/lists/${id}/folder`, { folderId })).data
}

export async function listFolders(): Promise<ListFolder[]> {
  return (await api.get<ListFolder[]>('/api/list-folders')).data
}

export async function createFolder(input: { name: string; color?: string | null }): Promise<ListFolder> {
  return (await api.post<ListFolder>('/api/list-folders', input)).data
}

export async function updateFolder(id: string, input: { name: string; color?: string | null }): Promise<ListFolder> {
  return (await api.put<ListFolder>(`/api/list-folders/${id}`, input)).data
}

export async function deleteFolder(id: string): Promise<void> {
  await api.delete(`/api/list-folders/${id}`)
}

export async function createList(input: ListInput): Promise<ContactList> {
  return (await api.post<ContactList>('/api/lists', input)).data
}

export async function updateList(id: string, input: ListInput): Promise<ContactList> {
  return (await api.put<ContactList>(`/api/lists/${id}`, input)).data
}

export async function deleteList(id: string): Promise<void> {
  await api.delete(`/api/lists/${id}`)
}

/** Manual lists only — a dynamic list responds 400 list.dynamic_members. */
export async function addMembers(listId: string, contactIds: string[]): Promise<{ added: number }> {
  return (await api.post<{ added: number }>(`/api/lists/${listId}/members`, { contactIds })).data
}

export async function getListContacts(listId: string): Promise<Contact[]> {
  return (await api.get<Contact[]>(`/api/lists/${listId}/contacts`)).data
}

/** Manual lists only — a dynamic list responds 400 list.dynamic_members. */
export async function removeMember(listId: string, contactId: string): Promise<void> {
  await api.delete(`/api/lists/${listId}/members/${contactId}`)
}

export async function sendToList(listId: string, input: SendMessageInput): Promise<BulkSendResult> {
  return (await api.post<BulkSendResult>(`/api/lists/${listId}/send`, input)).data
}
